import { useEffect, useId, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import type { GeoPoint } from '@app/shared/geo';
import { api, ApiError } from '../api';
import { isMapsLink, type PickedPlace } from './place';

type VenueSuggestion = { kind: 'venue'; venueId: string; name?: string; address?: string; point?: GeoPoint | null; googlePlaceId?: string; notes?: string };
type GoogleSuggestion = { kind: 'google'; placeId: string; main: string; secondary: string };
type Suggestion = VenueSuggestion | GoogleSuggestion;
type Answer = { suggestions: Suggestion[]; attribution: string; unavailable?: string };

/** How long typing must pause before suggestions are asked (each one may cost money at Google). */
export const PLACE_DEBOUNCE_MS = 250;
const MIN_LETTERS = 3;

/** A session token for Google's billing: one per search, a new one after a pick. */
const newSession = (): string => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `s${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`);

const optionKey = (s: Suggestion) => (s.kind === 'venue' ? `v:${s.venueId}` : `g:${s.placeId}`);

/**
 * The name of the place with suggestions while typing (owner 2026-10-21 22:46; spec 2026-10-22-mapy-google §3.1):
 * the company's places ("Miejsca wydarzeń", with their notes) first, then Google's — through the server, with a
 * session token. Picking one gives its name, address, point and ids; a pasted Google Maps link is read by the server.
 * A combobox (WAI-ARIA): ↑/↓ choose, Enter takes, Esc closes the list.
 */
export function PlaceSearch({
  label,
  value,
  onChange,
  onPick,
  placeholder,
  selectOnFocus = false,
}: {
  label: string;
  value: string;
  onChange: (name: string) => void;
  onPick: (place: PickedPlace) => void;
  placeholder?: string;
  /** A filled place (owner 2026-10-30: the default or a rule's): getting into the field selects it, so typing replaces it. */
  selectOnFocus?: boolean;
}) {
  const id = useId();
  const listId = `${id}-list`;
  const [answer, setAnswer] = useState<{ query: string; data: Answer } | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const session = useRef(newSession());
  const typed = useRef(false);
  /** Selected on focus: the click's mouseup must not put the caret back. */
  const selectedOnFocus = useRef(false);
  const inflight = useRef<AbortController | null>(null);

  const query = value.trim();
  useEffect(() => {
    // only what the person types asks (not a name set by a pick, nor one loaded with the event)
    if (!typed.current || query.length < MIN_LETTERS || isMapsLink(query)) return;
    const timer = setTimeout(() => {
      inflight.current?.abort();
      const mine = new AbortController();
      inflight.current = mine;
      api<Answer>(`/api/geo/suggest?q=${encodeURIComponent(query)}&session=${encodeURIComponent(session.current)}`, { signal: mine.signal })
        .then((data) => {
          if (mine.signal.aborted) return;
          setAnswer({ query, data });
          setActive(0);
          setError(data.unavailable ?? '');
        })
        .catch((e: unknown) => {
          if (!mine.signal.aborted) setError(e instanceof ApiError ? e.message : 'Nie udało się pobrać podpowiedzi');
        });
    }, PLACE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);
  useEffect(() => () => inflight.current?.abort(), []);

  const options = answer?.query === query ? answer.data.suggestions : [];
  const expanded = open && options.length > 0;
  const google = options.some((o) => o.kind === 'google');

  function finish(place: PickedPlace) {
    typed.current = false;
    session.current = newSession();
    setAnswer(null);
    setOpen(false);
    setStatus('');
    onPick(place);
  }

  async function pick(s: Suggestion) {
    setError('');
    if (s.kind === 'venue') {
      finish({ name: s.name ?? '', address: s.address ?? null, point: s.point ?? null, googlePlaceId: s.googlePlaceId ?? '', venueId: s.venueId, notes: s.notes ?? '' });
      return;
    }
    setStatus('Pobieram dane miejsca…');
    try {
      const p = await api<{ placeId: string; name: string; address: string; point: GeoPoint }>(`/api/geo/place/${encodeURIComponent(s.placeId)}?session=${encodeURIComponent(session.current)}`);
      finish({ name: p.name || s.main, address: p.address || null, point: p.point, googlePlaceId: p.placeId, venueId: null, notes: '' });
    } catch (e) {
      setStatus('');
      setError(e instanceof ApiError ? e.message : 'Nie udało się pobrać danych miejsca');
    }
  }

  async function readLink(url: string) {
    setOpen(false);
    setError('');
    setStatus('Odczytuję link z Map Google…');
    try {
      const p = await api<{ name: string; point: GeoPoint | null; placeId: string | null; address: string | null }>('/api/geo/link', { method: 'POST', body: { url } });
      finish({ name: p.name, address: p.address, point: p.point, googlePlaceId: p.placeId ?? '', venueId: null, notes: '' });
    } catch (e) {
      setStatus('');
      setError(e instanceof ApiError ? e.message : 'Nie udało się odczytać linku');
    }
  }

  function onPaste(e: ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData('text').trim();
    if (!isMapsLink(text)) return;
    // the link is read, not typed into the name
    e.preventDefault();
    void readLink(text);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (options.length === 0) return;
      e.preventDefault();
      setOpen(true);
      setActive((a) => (a + (e.key === 'ArrowDown' ? 1 : options.length - 1)) % options.length);
    } else if (e.key === 'Enter') {
      if (isMapsLink(query)) {
        e.preventDefault();
        onChange('');
        void readLink(query);
      } else if (expanded) {
        e.preventDefault();
        void pick(options[Math.min(active, options.length - 1)]!);
      }
    } else if (e.key === 'Escape' && expanded) {
      // the list closes; Esc must not also close the dialog or panel around the field
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    }
  }

  let lastKind: Suggestion['kind'] | null = null;
  return (
    <div className="field place-search">
      <label htmlFor={`${id}-input`}>{label}</label>
      <div className="place-search-box">
        <input
          id={`${id}-input`}
          type="text"
          role="combobox"
          aria-expanded={expanded}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={expanded ? `${id}-opt-${active}` : undefined}
          aria-describedby={`${id}-status`}
          autoComplete="off"
          spellCheck={false}
          value={value}
          placeholder={placeholder}
          onChange={(e) => {
            typed.current = true;
            onChange(e.target.value);
            setOpen(true);
          }}
          onPaste={onPaste}
          onKeyDown={onKeyDown}
          onFocus={(e) => {
            setOpen(true);
            if (!selectOnFocus) return;
            e.currentTarget.select();
            selectedOnFocus.current = true;
          }}
          onMouseUp={(e) => {
            if (selectedOnFocus.current) e.preventDefault();
            selectedOnFocus.current = false;
          }}
          onBlur={() => {
            setOpen(false);
            selectedOnFocus.current = false;
          }}
        />
        <ul id={listId} role="listbox" aria-label="Podpowiedzi miejsc" className="place-suggestions" hidden={!expanded}>
          {expanded &&
            options.flatMap((s, i) => {
              const heading = s.kind !== lastKind ? (
                <li key={`h-${s.kind}`} role="presentation" className="place-group">
                  {s.kind === 'venue' ? 'Miejsca wydarzeń' : 'Google'}
                </li>
              ) : null;
              lastKind = s.kind;
              const main = s.kind === 'venue' ? (s.name ?? '') : s.main;
              const secondary = s.kind === 'venue' ? (s.address ?? '') : s.secondary;
              return [
                heading,
                <li
                  key={optionKey(s)}
                  id={`${id}-opt-${i}`}
                  role="option"
                  aria-selected={i === active}
                  className={i === active ? 'active' : undefined}
                  // keep the focus in the field; the click picks
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => void pick(s)}
                >
                  <span className="place-main">{main}</span>
                  {secondary && <span className="place-secondary">{secondary}</span>}
                  {s.kind === 'venue' && s.notes && <span className="place-notes-hint">{s.notes}</span>}
                </li>,
              ];
            })}
          {expanded && google && (
            <li role="presentation" className="place-attribution">
              <span translate="no">Google Maps</span>
            </li>
          )}
        </ul>
      </div>
      {/* a live region without the role "status": a form has its own status lines */}
      <span id={`${id}-status`} className="hint" aria-live="polite">
        {error ? '' : status}
      </span>
      {error && (
        <span className="field-error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
